import { useEffect, useMemo, useState } from 'react';

/**
 * Render a long list a screenful at a time.
 *
 * Every row is a card with its own icons and badges, so a few hundred of them is
 * a large DOM. On a phone that is already short of memory that is what pushes
 * Android into killing the WebView's renderer, and the screen goes blank mid-use.
 * Visits in particular grow fast - the API will return up to a thousand.
 *
 * The window resets whenever the underlying list changes, so searching or
 * filtering starts from the top rather than leaving an odd offset behind.
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * The slicing itself, free of React so it can be tested on its own.
 * Slicing is skipped entirely when the whole list fits, so a short list is passed
 * straight through rather than copied.
 */
export const windowOf = (items, limit) => {
  const list = items || [];
  const capped = Math.max(0, limit);
  const shown = capped >= list.length ? list : list.slice(0, capped);
  const remaining = list.length - shown.length;
  return { shown, remaining, total: list.length, hasMore: remaining > 0 };
};

export const useWindowedList = (items, pageSize = DEFAULT_PAGE_SIZE) => {
  const list = useMemo(() => items || [], [items]);
  const [limit, setLimit] = useState(pageSize);

  // A different list - a new search, fresh data - starts again from the top.
  useEffect(() => {
    setLimit(pageSize);
  }, [list.length, pageSize]);

  return {
    ...windowOf(list, limit),
    showMore: () => setLimit((current) => current + pageSize),
    showAll: () => setLimit(list.length),
  };
};

export default useWindowedList;
